/**
 * Tata letak lembar log — track apa yang tergambar, dan bagaimana.
 *
 * Sebelumnya susunan track adalah sebuah konstanta di dalam layar
 * Workstation: lima track, kurva yang sudah ditentukan, warna dari tabel
 * tetap, dan satu ketebalan garis untuk semuanya. Itu cukup untuk memandang
 * sebuah sumur, tetapi bukan untuk bekerja pada sumur: seorang petrofisikawan
 * yang ingin menaruh DTC bersebelahan dengan RHOB, atau membaca RT pada
 * rentang 0,2–200 alih-alih 0,2–2000, tidak punya jalan sama sekali.
 *
 * Modul ini memindahkan susunan itu menjadi data — sesuatu yang bisa
 * ditambah, dikurangi, dan disimpan — dan menyediakan aritmetika sumbu yang
 * dipakai bersama oleh penggambar kurva dan pembaca skalanya, supaya angka
 * yang tercetak di kepala track memang angka yang dipakai menggambar.
 */

/** Sumbu mendatar sebuah track. */
export type AxisScale = "linear" | "log";

/** Jenis kurva — bentuk garisnya, bukan isinya. */
export type CurveStyle = "solid" | "dashed" | "dotted" | "dashdot";

/**
 * Acuan arsiran sebuah kurva: tepi mana dari skalanya yang menjadi garis dasar.
 *
 * `"max"` mengisi ruang antara kurva dan ujung nilai tertinggi skalanya —
 * separuh serpih sebuah track GR. `"min"` mengisi ke ujung terendah, yang
 * dicari oleh penanda lapisan rapat atau porositas kecil. Acuannya adalah
 * ujung skala kurva itu sendiri, bukan pecahan lebar track, sehingga satu
 * setelan tetap benar ketika skalanya diubah, dibalik, atau dijadikan
 * logaritmik.
 */
export type FillAnchor = "min" | "max";

/** Arsiran satu kurva terhadap salah satu ujung skalanya. */
export interface CurveFill {
  from: FillAnchor;
  /**
   * Warna arsiran. Kosong berarti mengikuti warna kurvanya sendiri, yang
   * hampir selalu benar: arsiran adalah kurva itu juga, dinyatakan sebagai
   * luasan. Diisi ketika luasan itu berarti sesuatu yang lain — pasir bersih
   * di bawah cutoff Vsh, misalnya, yang bukan berwarna kurva Vsh.
   */
  color?: string;
  /**
   * Pita warna, bila arsirannya diminta bergradasi menurut nilai kurvanya.
   *
   * Arsiran berwarna tunggal hanya menyatakan "di sini ada", dan seluruh
   * luasannya sama pekat baik pada Vsh 0,05 maupun 0,40. Dengan ramp, warna
   * di setiap kedudukan mendatar mengikuti nilai yang memang berada di sana,
   * sehingga satu luasan menyatakan dua hal sekaligus: seberapa jauh dan
   * seberapa besar. Kosong berarti warna tunggal, dan itu tetap bawaannya.
   */
  ramp?: string;
  /** Membalik arah rampnya, sebagaimana pita warna track. */
  reverse?: boolean;
  /**
   * Ke mana warnanya mengikuti nilai.
   *
   * `"across"` mewarnai menurut kedudukan mendatar: satu luasan terbentang dari
   * garis dasarnya sampai bacaannya, dan warnanya menyapu ramp di sepanjang
   * lebar itu. Yang menyatakan bacaan di sebuah kedalaman adalah tepi
   * luarnya — pinggir seluas satu-dua piksel.
   *
   * `"value"` mewarnai seluruh lebar luasan itu dengan satu warna: warna
   * bacaannya di kedalaman tersebut. Ini yang dipakai log terarsir di volume
   * 3D, dan alasannya sama — di sana sebuah lapisan tipis berporositas tinggi
   * harus terbaca sebagai warna, bukan sebagai lebar sepersekian milimeter.
   * Pada lembar log ia membuat satu track membawa dua bacaan sekaligus:
   * bentuk kurvanya, dan nilainya pada skala warna yang sama dengan model.
   *
   * Kosong berarti `"across"`, sebagaimana lembar yang sudah tersimpan.
   */
  by?: "across" | "value";
  /**
   * Warna sisi seberangnya: dari kurva ke ujung skala yang lain.
   *
   * Lembar korelasi cetak mengarsir GR di kedua sisinya — kuning di sisi
   * pasir, abu-abu di sisi serpih — sehingga seluruh lebar track terbaca
   * sebagai litologi dan bukan hanya separuhnya. Selalu berwarna tunggal:
   * ramp sudah mewarnai sisi utama, dan dua ramp dalam satu track adalah dua
   * skala warna yang harus dibaca sekaligus. Kosong berarti satu sisi saja.
   */
  opposite?: string;
}

/** Arsiran silang antara dua kurva pada track yang sama. */
export interface CrossFill {
  /** Kurva yang harus berada di sisi nilai lebih besar agar terarsir. */
  curve: string;
  /** Kurva pembandingnya. */
  against: string;
  color: string;
}

/**
 * Pita warna sebuah track — kurva yang digambar sebagai warna, bukan garis.
 *
 * Sebuah garis melaporkan satu angka dengan tepat dan memaksa mata menelusuri
 * bentuknya; sebuah pita warna melaporkan angka yang sama dengan kasar tetapi
 * memperlihatkan susunan lapisan sepanjang seratus meter sekaligus. Keduanya
 * dibutuhkan, dan yang kedua sebelumnya hanya ada di lembar korelasi.
 *
 * `min` dan `max` adalah jepitan warna dalam satuan kurva itu sendiri, bukan
 * pecahan. Yang membuat sebuah pita terbaca justru jepitannya: merentangkan
 * ramp ke seluruh rentang data menyerahkan hampir seluruh paletnya kepada
 * segelintir pencilan dan meninggalkan bagian tengah — yang sedang dibaca —
 * dalam satu warna rata. Kosong berarti mengikuti skala tampilan kurvanya.
 */
export interface SpectrumConfig {
  /** Kurva yang diwarnai. Kosong berarti kurva pertama track. */
  curve?: string;
  /** Nama ramp di `lib/colorscale`. */
  ramp: string;
  reverse?: boolean;
  min?: number;
  max?: number;
  /**
   * Lebar batang ikut nilainya juga, seperti track permeabilitas lembar
   * korelasi. Bawaannya mati: pita selebar penuh adalah sebuah log citra, dan
   * itu yang dicari orang yang meminta "color bar". Dinyalakan, warna dan
   * lebar melaporkan besaran yang sama dua kali — mubazir secara informasi,
   * dan justru itu yang membuat sebuah lapisan terbaca dari jarak satu meter.
   */
  bars?: boolean;
}

export interface CurveConfig {
  /** Mnemonic kurva, sebagaimana tersimpan di LAS. */
  name: string;
  /** Warna gambar. Kosong berarti warna bawaan tema untuk mnemonic ini. */
  color?: string;
  style?: CurveStyle;
  /** Ketebalan garis dalam piksel. Kosong berarti ikut ketebalan lembar. */
  width?: number;
  /** Skala mendatar. Kosong berarti skala tampilan yang tersimpan pada kurva. */
  min?: number;
  max?: number;
  hidden?: boolean;
  /** Arsiran ke salah satu ujung skala kurva ini. Kosong berarti tanpa arsiran. */
  fill?: CurveFill;
}

export interface TrackConfig {
  /** Tetap sepanjang umur track, juga ketika judulnya diganti. */
  id: string;
  title: string;
  units: string;
  /** Bobot lebar terhadap track lain di lembar yang sama. */
  flex: number;
  scale: AxisScale;
  /** Kolom litologi, digambar dari VSH alih-alih dari kurva. */
  lithology?: boolean;
  /**
   * Kolom litologi yang **dideskripsi** — dari core atau cutting, bukan
   * diturunkan dari kurva.
   *
   * Berdiri sendiri dan tidak menggantikan `lithology`, sebab keduanya
   * menjawab pertanyaan yang berbeda: yang satu apa yang tersirat dari log,
   * yang lain apa yang benar-benar dilihat orang pada batuannya. Sebuah
   * lembar yang menampilkan keduanya berdampingan memperlihatkan tempat
   * keduanya tidak sepakat — dan justru di situlah yang menarik.
   */
  described?: boolean;
  /**
   * Kolom kelas LRLC, digambar dari kurva `LRLC_CLASS` yang diterbitkan.
   *
   * Berdiri sendiri di sebelah kedua kolom litologi dengan alasan yang sama
   * seperti keduanya berdiri berdampingan: ia menjawab pertanyaan yang lain
   * lagi. Litologi mengatakan batuannya apa; kolom ini mengatakan di mana
   * resistivitasnya berbohong tentang isinya.
   */
  classes?: boolean;
  /**
   * Apa yang digambar kolom ini ketika ia memang tidak memuat kurva.
   *
   * Dipakai kolom yang isinya tidak lahir dari sebuah mnemonik — pita kelas,
   * bendera reservoir dan pay, selang uji produksi — dan ditulis oleh lembar
   * yang memuatnya, bukan oleh penyunting: hanya lembar itu yang tahu dari
   * mana pitanya datang. Tanpanya rel setelan melaporkan keempatnya sebagai
   * "Empty", dan sebuah kolom yang mengaku kosong adalah kolom yang dibuang
   * orang berikutnya karena disangka sisa.
   */
  note?: string;
  /** Pita warna. Ada berarti track ini digambar sebagai warna, bukan garis. */
  spectrum?: SpectrumConfig;
  /** Arsiran silang antara dua kurva — volume pori hidrokarbon. */
  shading?: CrossFill;
  curves: CurveConfig[];
}

export const CURVE_STYLES: { value: CurveStyle; label: string }[] = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "dashdot", label: "Dash-dot" },
];

/**
 * Pola garis putus, dinyatakan dalam satuan viewBox track.
 *
 * Ditulis relatif terhadap ketebalan supaya garis 2,5 px tidak berubah
 * menjadi garis utuh: pada goresan tebal, jeda 3 satuan tertutup oleh
 * goresannya sendiri.
 */
export function dashArrayFor(style: CurveStyle | undefined, width: number): string | undefined {
  const w = Math.max(0.6, width) / 1.3;
  const n = (value: number) => (value * w).toFixed(2);
  switch (style) {
    case "dashed":
      return `${n(5)} ${n(3)}`;
    case "dotted":
      return `${n(0.6)} ${n(2.6)}`;
    case "dashdot":
      return `${n(6)} ${n(2.6)} ${n(0.6)} ${n(2.6)}`;
    default:
      return undefined;
  }
}

/** Warna bawaan per mnemonic — tetap sebagai token tema, bukan heksadesimal. */
export const CURVE_COLORS: Record<string, string> = {
  GR: "var(--curve-gr)",
  SP: "var(--curve-sp)",
  RT: "var(--curve-rt)",
  RXO: "var(--curve-rxo)",
  RHOB: "var(--curve-rhob)",
  NPHI: "var(--curve-nphi)",
  PHIE: "var(--curve-phie)",
  PHIT: "var(--accent-deep)",
  SW: "var(--curve-sw)",
  VSH: "var(--green)",
  DTC: "var(--violet)",
};

export function defaultCurveColor(mnemonic: string, stored?: string): string {
  return CURVE_COLORS[mnemonic] ?? (stored || "var(--ink-3)");
}

/** Mnemonic yang lazimnya dibaca pada sumbu logaritmik. */
const LOG_BY_NATURE = new Set([
  "RT", "RXO", "RD", "RS", "ILD", "LLD", "LLS", "MSFL", "PERM", "K",
  // Ketiganya turunan permeabilitas dan mewarisi dekadenya. ROCKTYPE tidak:
  // ia nomor kelas, dan sumbu logaritmik atas nomor kelas menempatkan RT 1
  // dan RT 2 sejauh RT 2 dan RT 4.
  "FZI", "RQI", "R35",
]);

export function suggestedScale(mnemonic: string): AxisScale {
  return LOG_BY_NATURE.has(mnemonic.toUpperCase()) ? "log" : "linear";
}

let counter = 0;

/** Pengenal track baru. Cukup unik untuk satu peramban, dan itu yang perlu. */
export function newTrackId(): string {
  counter += 1;
  return `t${Date.now().toString(36)}-${counter.toString(36)}`;
}

/** Susunan bawaan: lima track dari mockup. */
export function defaultTracks(): TrackConfig[] {
  return [
    {
      id: "gr-sp",
      title: "GR / SP",
      units: "gAPI",
      flex: 1.15,
      scale: "linear",
      curves: [{ name: "GR", style: "solid" }, { name: "SP", style: "dashed" }],
    },
    {
      id: "res",
      title: "Resistivity",
      units: "ohm·m",
      flex: 1.15,
      scale: "log",
      curves: [{ name: "RT", style: "solid" }, { name: "RXO", style: "dashed" }],
    },
    {
      id: "rhob-nphi",
      title: "RHOB / NPHI",
      units: "g/cc · v/v",
      flex: 1.15,
      scale: "linear",
      curves: [{ name: "RHOB", style: "solid" }, { name: "NPHI", style: "dashed" }],
    },
    {
      id: "phie-sw",
      title: "PHIE / SW",
      units: "v/v",
      flex: 1.1,
      scale: "linear",
      // Semua yang di kiri SW di dalam PHIE adalah volume pori hidrokarbon.
      shading: { curve: "PHIE", against: "SW", color: "var(--green)" },
      curves: [{ name: "PHIE", style: "solid" }, { name: "SW", style: "solid" }],
    },
    {
      id: "vsh",
      title: "Vsh / Lithology",
      units: "fraction",
      flex: 0.62,
      scale: "linear",
      lithology: true,
      curves: [{ name: "VSH", style: "solid" }],
    },
  ];
}

const STYLE_VALUES = new Set<string>(CURVE_STYLES.map((option) => option.value));

/**
 * Membaca kembali susunan yang tersimpan di localStorage.
 *
 * Yang tersimpan di sana adalah JSON dari versi aplikasi mana pun yang
 * terakhir dipakai, jadi tidak satu pun medannya boleh dipercaya. Susunan
 * yang tidak bisa dipulihkan mengembalikan `null` — pemanggil lalu memakai
 * bawaan — sebab lembar log yang kosong karena satu medan bertipe salah
 * adalah kegagalan yang tak terbaca oleh penggunanya.
 */
export function normalizeTracks(raw: unknown): TrackConfig[] | null {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<string>();
  const tracks: TrackConfig[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const source = item as Record<string, unknown>;
    const curves = Array.isArray(source.curves)
      ? source.curves
          .map((entry): CurveConfig | null => {
            if (!entry || typeof entry !== "object") return null;
            const curve = entry as Record<string, unknown>;
            if (typeof curve.name !== "string" || !curve.name) return null;
            return {
              name: curve.name,
              color: typeof curve.color === "string" && curve.color ? curve.color : undefined,
              style: typeof curve.style === "string" && STYLE_VALUES.has(curve.style)
                ? (curve.style as CurveStyle)
                : "solid",
              width: numberOrUndefined(curve.width, 0.4, 6),
              min: numberOrUndefined(curve.min),
              max: numberOrUndefined(curve.max),
              hidden: curve.hidden === true,
              fill: readFill(curve.fill),
            };
          })
          .filter((curve): curve is CurveConfig => curve !== null)
      : [];
    const lithology = source.lithology === true;
    const classes = source.classes === true;
    const described = source.described === true;
    // Track tanpa kurva adalah track yang baru ditambahkan dan belum diisi,
    // bukan sampah. Yang dibuang di sini hanyalah yang tidak menyerupai track
    // sama sekali — `curves` yang bukan larik. Sebelumnya keduanya dibuang
    // bersama, dan karena susunan yang berlaku dibaca kembali dari
    // localStorage pada setiap render, penjagaan terhadap JSON asing itu ikut
    // menghapus track kosong yang baru saja ditulis aplikasi ini sendiri:
    // "+ Track baru" menyimpan tracknya lalu kehilangannya seketika, dan yang
    // terbaca adalah tombol yang tidak melakukan apa pun.
    if (!Array.isArray(source.curves) && !lithology && !described && !classes) continue;
    let id = typeof source.id === "string" && source.id ? source.id : newTrackId();
    while (seen.has(id)) id = newTrackId();
    seen.add(id);
    // Sampai versi 2.3 arsiran adalah milik track dan selalu mengenai kurva
    // pertamanya. Susunan yang tersimpan dari versi itu tetap harus terbaca,
    // jadi setelannya dipindahkan ke kurva yang dulu memang diarsirnya —
    // membuangnya diam-diam berarti lembar yang dibuka kembali kehilangan
    // arsiran tanpa sepatah kata pun.
    if ((source.fill === "max" || source.fill === "min") && curves.length && !curves[0].fill) {
      curves[0] = { ...curves[0], fill: { from: source.fill } };
    }
    tracks.push({
      id,
      title: typeof source.title === "string" && source.title ? source.title : "Track",
      units: typeof source.units === "string" ? source.units : "",
      flex: numberOrUndefined(source.flex, 0.3, 4) ?? 1,
      scale: source.scale === "log" ? "log" : "linear",
      lithology: lithology || undefined,
      classes: classes || undefined,
      described: described || undefined,
      spectrum: readSpectrum(source.spectrum),
      shading: readShading(source.shading),
      curves,
    });
  }
  // Satu track kosong di antara track yang berisi adalah track yang sedang
  // dibuat. Susunan yang tak satu pun tracknya menggambar apa pun bukan:
  // lembar yang kosong seluruhnya terbaca sebagai kerusakan, bukan sebagai
  // "belum diisi", jadi pemanggilnya lebih baik memakai susunan bawaan.
  return tracks.some(
    (track) => track.curves.length > 0 || track.lithology || track.described
      || track.classes,
  ) ? tracks : null;
}

function numberOrUndefined(value: unknown, min?: number, max?: number): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  if (min !== undefined && value < min) return min;
  if (max !== undefined && value > max) return max;
  return value;
}

/** Arsiran satu kurva yang tersimpan, dibaca dengan curiga seperti sisanya. */
function readFill(value: unknown): CurveFill | undefined {
  if (!value || typeof value !== "object") return undefined;
  const fill = value as Record<string, unknown>;
  if (fill.from !== "min" && fill.from !== "max") return undefined;
  return {
    from: fill.from,
    color: typeof fill.color === "string" && fill.color ? fill.color : undefined,
    // Nama ramp dibaca apa adanya, tidak diperiksa terhadap daftar yang ada:
    // yang menggambar sudah mundur ke ramp bawaan untuk nama yang tak
    // dikenalnya, dan sebuah susunan dari versi yang punya ramp lebih banyak
    // lebih baik kehilangan warnanya daripada kehilangan arsirannya.
    ramp: typeof fill.ramp === "string" && fill.ramp ? fill.ramp : undefined,
    reverse: fill.reverse === true ? true : undefined,
    // Hanya "value" yang berarti sesuatu di sini; apa pun selainnya, termasuk
    // susunan dari versi yang belum mengenal medan ini, berarti melintang.
    by: fill.by === "value" ? "value" : undefined,
  };
}

/**
 * Pita warna yang tersimpan, dibaca dengan curiga.
 *
 * Nama ramp tidak diperiksa terhadap daftar ramp yang ada: `makePainter`
 * sudah memulangkan ramp bawaan untuk nama yang tak dikenalnya, dan menolak
 * seluruh setelan pita hanya karena satu nama ramp berubah antarversi berarti
 * membuang jepitan yang justru sulit disetel ulang.
 */
function readSpectrum(value: unknown): SpectrumConfig | undefined {
  if (!value || typeof value !== "object") return undefined;
  const source = value as Record<string, unknown>;
  if (typeof source.ramp !== "string" || !source.ramp) return undefined;
  return {
    curve: typeof source.curve === "string" && source.curve ? source.curve : undefined,
    ramp: source.ramp,
    reverse: source.reverse === true || undefined,
    min: numberOrUndefined(source.min),
    max: numberOrUndefined(source.max),
    bars: source.bars === true || undefined,
  };
}

/**
 * Arsiran silang yang tersimpan, dibaca dengan curiga seperti sisanya.
 *
 * Warnanya dilucuti alfanya. Sampai versi 2.3 arsiran silang adalah satu
 * konstanta `rgba(...,.22)` yang membawa tembus pandangnya sendiri, dan
 * tembus pandang itu kini milik yang menggambar — sebuah warna yang dipilih
 * dari palet adalah warna pekat, dan mengalikan dua alfa akan meninggalkan
 * luasan yang tak terlihat tanpa memberi tahu siapa pun bahwa ia ada.
 */
function readShading(value: unknown): CrossFill | undefined {
  if (!value || typeof value !== "object") return undefined;
  const shading = value as Record<string, unknown>;
  if (typeof shading.curve !== "string" || !shading.curve) return undefined;
  if (typeof shading.against !== "string" || !shading.against) return undefined;
  if (typeof shading.color !== "string" || !shading.color) return undefined;
  return { curve: shading.curve, against: shading.against, color: opaque(shading.color) };
}

/** `rgba(r,g,b,a)` menjadi `rgb(r,g,b)`; warna dalam bentuk lain apa adanya. */
export function opaque(color: string): string {
  const parts = /^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*[\d.]+\s*\)$/i.exec(
    color.trim(),
  );
  return parts ? `rgb(${parts[1]}, ${parts[2]}, ${parts[3]})` : color;
}

/** Track kosong yang baru, ditaruh di ujung kanan lembar. */
export function makeTrack(mnemonic?: string, unit?: string): TrackConfig {
  return {
    id: newTrackId(),
    title: mnemonic ?? "Track baru",
    units: unit ?? "",
    flex: 1,
    scale: mnemonic ? suggestedScale(mnemonic) : "linear",
    curves: mnemonic ? [{ name: mnemonic, style: "solid" }] : [],
  };
}

/**
 * Kolom litologi yang dideskripsi, sebagai track baru.
 *
 * Ia tidak bisa dibuat lewat "track baru dari kurva" seperti track lain,
 * sebab justru tidak ada kurva yang menjadi isinya: yang digambar adalah
 * selang yang ditulis orang setelah melihat batuannya. Lebarnya menyusul
 * kolom litologi dari VSH, supaya keduanya berdampingan sama lebar ketika
 * lembar yang sama memuat keduanya.
 */
export function makeDescribedTrack(): TrackConfig {
  return {
    id: newTrackId(),
    title: "Described lithology",
    units: "",
    flex: 0.62,
    scale: "linear",
    described: true,
    curves: [],
  };
}

/**
 * Kolom kelas LRLC, selebar kolom litologi supaya ketiganya sepadan.
 *
 * Ia tidak membaca kurva lewat `curves`: nilainya dibaca dari `LRLC_CLASS`
 * yang sudah diterbitkan, dan digambar sebagai pita warna dan bukan sebagai
 * garis. Sebuah garis yang naik turun di antara nol dan enam terbaca seperti
 * besaran yang punya urutan, padahal nomor kelas tidak punya jarak: "coal"
 * bukan enam kali "water".
 */
export function makeLrlcTrack(): TrackConfig {
  return {
    id: newTrackId(),
    title: "LRLC class",
    units: "",
    flex: 0.62,
    scale: "linear",
    classes: true,
    curves: [],
  };
}

export function addTrack(tracks: TrackConfig[], track: TrackConfig): TrackConfig[] {
  return [...tracks, track];
}

export function removeTrack(tracks: TrackConfig[], id: string): TrackConfig[] {
  return tracks.filter((track) => track.id !== id);
}

/** Menggeser satu track satu tempat ke kiri (`-1`) atau ke kanan (`1`). */
/**
 * Memindahkan sebuah anggota ke tempat anggota lain — bukan menukar keduanya.
 *
 * `moveTrack` di bawah menukar dua tetangga, dan itu benar untuk tombol kiri
 * dan kanan: satu tekanan, satu langkah. Menyeret track kelima ke tempat
 * pertama bukan penukaran — track yang tergeser adalah keempat track yang
 * dilewatinya, masing-masing bergeser satu tempat, dan menukarnya dengan yang
 * pertama akan melempar yang pertama ke tempat kelima: sebuah lembar yang
 * susunannya tidak diminta siapa pun.
 */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** `moveItem` dengan id, sebab yang tergambar boleh jadi bukan seluruh susunan. */
export function moveTrackTo(tracks: TrackConfig[], id: string, targetId: string): TrackConfig[] {
  return moveItem(
    tracks,
    tracks.findIndex((track) => track.id === id),
    tracks.findIndex((track) => track.id === targetId),
  );
}

/**
 * Track yang berada di bawah sebuah tapak, dibaca dari kotak-kotak kepalanya.
 *
 * Yang dipakai adalah kotak sebelum seretan dimulai, dan dengan sengaja: kotak
 * yang diukur ulang sepanjang seretan adalah kotak yang bergerak karena
 * seretan itu sendiri, dan track yang bertukar tempat di bawah tapak yang diam
 * akan bertukar lagi, dan lagi.
 *
 * Di luar track pertama dan terakhir tapaknya menempel pada yang terdekat:
 * seretan yang keluar sedikit dari lembar adalah seretan yang menuju ujung,
 * bukan seretan yang batal.
 */
export function trackAt(boxes: { left: number; width: number }[], x: number): number {
  if (boxes.length === 0) return -1;
  for (let index = 0; index < boxes.length; index += 1) {
    if (x < boxes[index].left + boxes[index].width) return index;
  }
  return boxes.length - 1;
}

export function moveTrack(tracks: TrackConfig[], id: string, direction: -1 | 1): TrackConfig[] {
  const index = tracks.findIndex((track) => track.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= tracks.length) return tracks;
  const next = [...tracks];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function updateTrack(
  tracks: TrackConfig[],
  id: string,
  patch: Partial<TrackConfig>,
): TrackConfig[] {
  return tracks.map((track) => (track.id === id ? { ...track, ...patch } : track));
}

export function updateCurve(
  tracks: TrackConfig[],
  id: string,
  index: number,
  patch: Partial<CurveConfig>,
): TrackConfig[] {
  return tracks.map((track) =>
    track.id === id
      ? { ...track, curves: track.curves.map((curve, i) => (i === index ? { ...curve, ...patch } : curve)) }
      : track,
  );
}

export function removeCurve(tracks: TrackConfig[], id: string, index: number): TrackConfig[] {
  return tracks.map((track) =>
    track.id === id ? { ...track, curves: track.curves.filter((_, i) => i !== index) } : track,
  );
}

export function addCurve(tracks: TrackConfig[], id: string, curve: CurveConfig): TrackConfig[] {
  return tracks.map((track) =>
    track.id === id && !track.curves.some((existing) => existing.name === curve.name)
      ? { ...track, curves: [...track.curves, curve] }
      : track,
  );
}

/**
 * Rentang sumbu yang benar-benar dipakai menggambar.
 *
 * Sumbu logaritmik tidak menerima nol maupun bilangan negatif, dan kurva yang
 * skalanya menyentuh nol pada sumbu seperti itu sebelumnya lenyap tanpa pesan
 * apa pun — `project` mengembalikan NaN dan setiap titiknya terbuang diam-diam.
 * Di sini rentangnya dinaikkan ke bilangan positif terdekat yang masuk akal,
 * sehingga yang terlihat adalah kurva pada skala yang sedikit berbeda, bukan
 * track yang kosong.
 */
export function resolveAxisRange(min: number, max: number, scale: AxisScale): [number, number] {
  let lo = Number.isFinite(min) ? min : 0;
  let hi = Number.isFinite(max) ? max : 1;
  if (lo === hi) hi = lo + (Math.abs(lo) || 1);
  if (scale !== "log") return [lo, hi];
  const magnitude = Math.max(Math.abs(lo), Math.abs(hi));
  const floor = magnitude > 0 ? magnitude / 10000 : 0.2;
  if (lo <= 0) lo = floor;
  if (hi <= 0) hi = floor;
  if (lo === hi) hi = lo * 10;
  return [lo, hi];
}

export interface AxisTick {
  value: number;
  /** Posisi pada lebar track, 0 di tepi kiri dan 1 di tepi kanan. */
  t: number;
  /** Garis dan angka penuh; yang bukan mayor hanya garis bantu. */
  major: boolean;
}

/**
 * Angka-angka yang tercetak di sepanjang sumbu mendatar sebuah track.
 *
 * Pada sumbu linear ini sekadar pembagian rata. Pada sumbu logaritmik yang
 * dibagi rata bukan skalanya melainkan kertasnya: garisnya jatuh di setiap
 * dekade, dengan garis bantu 2–9 selama dekadenya masih sedikit. Kisi lama
 * menggambar empat garis berjarak sama pada track resistivitas, yang berarti
 * seorang pembaca yang menghitung sepanjang kisi itu membaca angka yang salah.
 */
export function axisTicks(
  min: number,
  max: number,
  scale: AxisScale,
  divisions = 4,
): AxisTick[] {
  const [lo, hi] = resolveAxisRange(min, max, scale);
  if (scale !== "log") {
    const steps = Math.max(1, Math.round(divisions));
    return Array.from({ length: steps + 1 }, (_, i) => ({
      value: lo + ((hi - lo) * i) / steps,
      t: i / steps,
      major: true,
    }));
  }
  const logLo = Math.log10(lo);
  const logHi = Math.log10(hi);
  const span = logHi - logLo;
  if (Math.abs(span) < 1e-12) return [{ value: lo, t: 0, major: true }];
  const first = Math.floor(Math.min(logLo, logHi));
  const last = Math.ceil(Math.max(logLo, logHi));
  const decades = last - first;
  const minors = decades <= 4 ? [2, 3, 4, 5, 6, 7, 8, 9] : [];
  const ticks: AxisTick[] = [];
  for (let d = first; d <= last; d++) {
    const decade = Math.pow(10, d);
    for (const factor of [1, ...minors]) {
      const value = decade * factor;
      const t = (Math.log10(value) - logLo) / span;
      if (t < -1e-9 || t > 1 + 1e-9) continue;
      ticks.push({ value, t: Math.min(1, Math.max(0, t)), major: factor === 1 });
    }
  }
  return ticks.sort((a, b) => a.t - b.t);
}

/** Angka skala di kepala track — pendek, sebab ruangnya selebar satu track. */
/** Satu angka skala sebagaimana ia akan tercetak: letaknya, dan tulisannya. */
export interface ScaleLabel {
  /** Letak pada lebar track, 0 di tepi kiri dan 1 di tepi kanan. */
  t: number;
  text: string;
}

/**
 * Angka skala yang benar-benar muat, dari yang seharusnya tercetak.
 *
 * Kepala track menaruh tiap angka pada pecahan lebarnya dan tidak pernah
 * bertanya apakah dua di antaranya bertumpang tindih. Pada track selebar
 * separuh layar memang tidak pernah; pada lembar LRLC, yang menjejalkan enam
 * belas kolom, kolom DT selebar lima puluh lima piksel diminta mencetak
 * `240 190 140 90 40` dan yang keluar adalah `240190 140 90 40` — dua angka
 * yang bersentuhan terbaca sebagai satu angka keenam yang tidak pernah ada,
 * dan `0.001` yang menimpa `0.01` di kolom RWA membuat sumbunya salah baca
 * satu dekade penuh. Bukan sekadar sesak: ia berbohong.
 *
 * Yang dibuang hanya **angkanya**, bukan garis kisinya. Garis kisi digambar
 * `LogTrack` dari daftar yang sama dan tetap utuh, jadi pembagiannya masih
 * terlihat — yang hilang cuma tulisan yang toh tak terbaca.
 *
 * Angka pertama dan terakhir tidak pernah dibuang, betapapun sempit tracknya.
 * Keduanya yang menyatakan rentang sumbu, dan sebuah sumbu tanpa ujung bukan
 * sumbu yang sesak melainkan sumbu yang tak punya arti sama sekali. Kalau
 * hanya keduanya yang tersisa, itu jawaban yang benar untuk kolom selebar itu.
 *
 * `width` nol berarti belum terukur — jsdom, dan render pertama sebelum
 * `ResizeObserver` berbunyi. Di situ semuanya dipulangkan apa adanya: menebak
 * lebar lalu membuang angka atas tebakan itu menghapus keterangan yang mungkin
 * sebenarnya muat.
 *
 * `charWidth` adalah lebar satu aksara pada fontnya — monospace 8 piksel maju
 * kira-kira 4,8 piksel per aksara. Diukur begitu dan bukan lewat `measureText`
 * sebab yang dibutuhkan hanya tahu apakah dua kotak bersentuhan, dan sebuah
 * kanvas ukur yang dipanggil tiap render untuk lima angka adalah ongkos yang
 * tidak dibayar oleh ketelitian yang tidak dipakai.
 */
export function thinScaleLabels(
  labels: ScaleLabel[],
  width: number,
  charWidth = 4.8,
  gap = 3,
): ScaleLabel[] {
  if (labels.length <= 2 || !(width > 0)) return labels;

  // Kotak yang benar-benar ditempati tulisannya, mengikuti `transform` yang
  // dipakai kepalanya: yang di ujung kiri dipatok kiri, yang di ujung kanan
  // dipatok kanan, sisanya dipusatkan pada garis kisinya.
  const box = (label: ScaleLabel): [number, number] => {
    const w = label.text.length * charWidth;
    const x = label.t * width;
    if (label.t <= 0.001) return [x, x + w];
    if (label.t >= 0.999) return [x - w, x];
    return [x - w / 2, x + w / 2];
  };

  const last = labels[labels.length - 1];
  const lastBox = box(last);
  const kept = [labels[0]];
  let right = box(labels[0])[1];
  for (const label of labels.slice(1, -1)) {
    const [lo, hi] = box(label);
    if (lo < right + gap) continue;
    if (hi + gap > lastBox[0]) continue;
    kept.push(label);
    right = hi;
  }
  kept.push(last);
  return kept;
}

export function formatScaleValue(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const magnitude = Math.abs(value);
  if (magnitude === 0) return "0";
  if (magnitude >= 10000 || magnitude < 0.001) return value.toExponential(0).replace("e+", "e");
  if (Number.isInteger(value)) return String(value);
  if (magnitude >= 100) return value.toFixed(0);
  if (magnitude >= 10) return value.toFixed(1);
  if (magnitude >= 1) return String(Number(value.toFixed(2)));
  return String(Number(value.toFixed(3)));
}

/**
 * Angka bacaan picker — ketelitian mengikuti besaran, bukan mengikuti daftar
 * mnemonic. GR 62,4 dan PHIE 0,183 keduanya keluar benar tanpa perlu tahu
 * kurva apa yang sedang dibaca, termasuk untuk kurva yang baru diimpor.
 */
export function formatReading(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const magnitude = Math.abs(value);
  if (magnitude >= 1000) return value.toFixed(0);
  if (magnitude >= 100) return value.toFixed(1);
  if (magnitude >= 10) return value.toFixed(2);
  return value.toFixed(3);
}

/**
 * Kolom litologi yang digambar dari Vsh.
 *
 * Serpih, lempungan, lanauan, pasir bersih — empat kelas yang batasnya adalah
 * batas yang dipakai orang membaca sebuah kurva Vsh, dan bukan sesuatu yang
 * disimpan di mana pun. Warnanya token tema, sehingga kolom yang sama terbaca
 * benar di lembar gelap maupun di tema "Core Paper" yang terang.
 *
 * Dipakai bersama oleh lembar log dan lembar hasil analisis: dua layar yang
 * menggambar litologi dari kurva yang sama dengan dua ambang yang berbeda
 * memperlihatkan dua sumur, bukan satu.
 */
export function lithologyFromVsh(
  vsh: { depths: number[]; values: (number | null)[] } | undefined,
): { top: number; base: number; fill: string }[] | undefined {
  if (!vsh || !vsh.depths.length) return undefined;
  const blocks: { top: number; base: number; fill: string }[] = [];
  const classify = (value: number) =>
    value > 0.55
      ? "var(--litho-shale)"
      : value > 0.35
        ? "var(--litho-clayey)"
        : value > 0.2
          ? "var(--litho-silty)"
          : "var(--litho-sand)";
  let start = vsh.depths[0];
  let current: string | null = null;
  for (let i = 0; i < vsh.depths.length; i++) {
    const value = vsh.values[i];
    const kind = value === null || !Number.isFinite(value) ? null : classify(value);
    if (kind !== current) {
      if (current) blocks.push({ top: start, base: vsh.depths[i], fill: current });
      current = kind;
      start = vsh.depths[i];
    }
  }
  if (current) blocks.push({ top: start, base: vsh.depths[vsh.depths.length - 1], fill: current });
  return blocks;
}

/**
 * Satu selang litologi yang dideskripsi, siap digambar.
 *
 * `label` adalah nama batuan yang tercetak di dalam pitanya bila pita itu
 * cukup tinggi memuatnya; `hint` keterangan lengkapnya, yang muncul ketika
 * kursor berhenti di atasnya.
 */
export interface DescribedBlock {
  top: number;
  base: number;
  fill: string;
  label: string;
  hint: string;
}

/** Satu baris deskripsi sebagaimana dipulangkan `/api/core-descriptions/`. */
export interface DescribedRow {
  top_md: number;
  base_md: number;
  lithology: string;
  grain_size?: string;
  structure?: string;
  color?: string;
  source_label?: string;
}

/**
 * Kolom litologi yang **dideskripsi** — dari core atau cutting.
 *
 * Bedanya dengan [lithologyFromVsh] bukan cuma sumbernya: yang di sana
 * disimpulkan dari sebuah kurva dan karena itu selalu menutupi seluruh sumur,
 * yang di sini adalah catatan orang dan karena itu berlubang — sumur dengan
 * dua core setebal sembilan meter tidak punya deskripsi di antara keduanya,
 * dan lubang itu memang harus terbaca sebagai "tidak dideskripsi", bukan
 * ditambal warna.
 */
export function describedLithology(rows: DescribedRow[]): DescribedBlock[] {
  return rows.map((row) => {
    // Selang yang tertulis terbalik tetap digambar. Tinggi pita dihitung
    // sebagai `base - top`, dan yang negatif dipulangkan CSS sebagai pita
    // yang tidak ada sama sekali: sebuah selang yang hilang tanpa sepatah
    // keluhan, pada kolom yang justru dipakai memeriksa kelengkapan.
    const top = Math.min(row.top_md, row.base_md);
    const base = Math.max(row.top_md, row.base_md);
    return {
      top,
      base,
      // Warna kosong bukan warna. Sebuah baris yang datang tanpa warna —
      // lewat impor, atau lewat baris yang ditulis sebelum medannya ada —
      // akan menjadi `background: ""` dan pitanya tembus pandang.
      fill: row.color || "#c8b273",
      // Utuh, dan dipendekkan oleh lebar pitanya sendiri ketika tidak muat.
      // Sebuah potongan sepanjang jumlah huruf yang tetap tidak tahu track
      // ini selebar apa: ia memenggal "Calcareous sandstone" menjadi
      // "Calcareous s" pada track yang sebenarnya sanggup memuat seluruhnya,
      // dan memenggalnya tanpa tanda bahwa ada yang dipenggal.
      label: row.lithology,
      // Keterangannya menyebut asalnya. Litologi cutting dan litologi core
      // tergambar dengan cara yang sama di kolom ini, sedangkan yang satu
      // dibaca dari serpih yang naik bersama lumpur dan yang lain dari batuan
      // yang dipegang orang — dan bobot keduanya tidak sama.
      hint: `${[row.lithology, row.grain_size, row.structure, row.source_label]
        .filter(Boolean)
        .join(" · ")} · ${top.toFixed(1)}–${base.toFixed(1)} m`,
    };
  });
}
